import { useState, useEffect, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminLogin,
  verifyAdmin,
  fetchAdminContent,
  saveContent,
  resetContent,
  getToken,
  clearToken,
} from "@/lib/admin";
import ContentEditor from "@/components/admin/ContentEditor";
import { Lock, LogOut, Save, RotateCcw, ExternalLink, Loader2, Eye, EyeOff } from "lucide-react";

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(password);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05050A] px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0B1220] p-8 space-y-5 shadow-2xl"
        data-testid="admin-login-form"
      >
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-xl bg-[#38BDF8]/15 flex items-center justify-center">
            <Lock className="w-6 h-6 text-[#38BDF8]" />
          </div>
          <h1 className="font-display text-xl uppercase tracking-tight text-white">
            Admin Access
          </h1>
          <p className="text-sm text-slate-400 text-center">
            Enter the password to edit site content.
          </p>
        </div>
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="pr-10"
            data-testid="admin-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-white"
            aria-label={showPassword ? "Hide password" : "Show password"}
            data-testid="admin-password-toggle"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {error && (
          <p className="text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading || !password}
          className="w-full"
          data-testid="admin-login-button"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [variant, setVariant] = useState("v1");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminContent(variant);
      setContent(data);
    } catch (e) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  }, [variant]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveContent(content, variant);
      setContent(saved);
      toast.success(`Variation ${variant.toUpperCase()} saved. Changes are live!`);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onLogout();
      } else {
        toast.error("Failed to save. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        `Reset ALL content of variation ${variant.toUpperCase()} back to the original defaults? This cannot be undone.`
      )
    )
      return;
    setSaving(true);
    try {
      const fresh = await resetContent(variant);
      setContent(fresh);
      toast.success(`Variation ${variant.toUpperCase()} reset to defaults.`);
    } catch (e) {
      toast.error("Failed to reset.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#05050A]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <h1 className="font-display text-lg uppercase tracking-tight">
            Content Admin
          </h1>
          <div className="flex items-center gap-2">
            <a
              href={variant === "v1" ? "/" : `/${variant}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white px-3 py-2"
              data-testid="admin-view-site"
            >
              <ExternalLink className="w-4 h-4" /> View site
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving || loading}
              data-testid="admin-reset-button"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || loading}
              className="bg-[#38BDF8] text-[#05050A] hover:bg-white"
              data-testid="admin-save-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" /> Save
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* variation switcher */}
        <div className="flex items-center gap-2 mb-6" data-testid="admin-variant-tabs">
          {["v1", "v2", "v3", "v4", "v5", "v6", "v7"].map((v) => (
            <button
              key={v}
              onClick={() => setVariant(v)}
              disabled={saving}
              data-testid={`admin-variant-tab-${v}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold uppercase tracking-wide border transition-colors ${
                variant === v
                  ? "bg-[#38BDF8] text-[#05050A] border-[#38BDF8]"
                  : "bg-transparent text-slate-400 border-white/15 hover:text-white hover:border-white/40"
              }`}
            >
              {v === "v5" ? "Sprint (v5)" : v === "v7" ? "Podium (v7)" : v === "v6" ? "Podium IA (v6)" : `Variation ${v.slice(1)}`}
            </button>
          ))}
          <span className="text-xs text-slate-500 ml-2">
            Link: {variant === "v1" ? "/" : `/${variant}`}
          </span>
        </div>
        {loading || !content ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-400 mb-6">
              Edit any text below and hit <strong>Save</strong>. Changes go live
              instantly, no redeploy needed.
            </p>
            <ContentEditor content={content} onChange={setContent} />
            <div className="mt-8 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#38BDF8] text-[#05050A] hover:bg-white"
                data-testid="admin-save-button-bottom"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" /> Save changes
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null = checking

  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    verifyAdmin()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#05050A]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" theme="dark" />
      {authed ? (
        <Dashboard onLogout={logout} />
      ) : (
        <LoginScreen onSuccess={() => setAuthed(true)} />
      )}
    </>
  );
}
